import React, { useEffect, useState } from "react";
import "../assets/templates/Maze.css";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import arrayMove from "array-move";
import LessonInstruction from "../containers/instruction/LessonInstruction";
import { t } from "../lib/i18n";
import { useRegisterProgress } from "../hooks/useRegisterProgress";

const getCardBackgroundClasses = (value, selectedItemsObj, index) => {
    let cardBackgroundClass = typeof value == "undefined"
        ? ""
        : value
            ? "right-answer"
            : "wrong-answer";

    if (selectedItemsObj && index !== undefined && selectedItemsObj[index]) {
        cardBackgroundClass += " maze-item-selected";
    }

    return "illustrated-word " + cardBackgroundClass;
}

// Coluna 1
const SortableItem1 = SortableElement(({ value1 }) => {
    const cardBackgroundClasses = getCardBackgroundClasses(value1.right1);
    const itemClasses = `${cardBackgroundClasses} disabled`;

    return (
        <div className={itemClasses}>
            {value1.en.split("/")[0]}
        </div>
    );
});

const SortableList1 = SortableContainer(({ items1 }) => {
    return (
        <ul className={"m-0 p-0"}>
            {items1.map((value1, index) => (
                <SortableItem1
                    key={`item-${value1.id}`}
                    index={index}
                    value1={value1}
                    sortIndex={index}
                    disabled={true}
                />
            ))}
        </ul>
    );
});

// Coluna 2
const SortableItem2 = SortableElement(({ value2, sortIndex, selectedItems2 }) => {
    return (
        <div className={getCardBackgroundClasses(value2.right2, selectedItems2, sortIndex)}>
            {value2.en.split("/")[1]}
        </div>
    )
});

const SortableList2 = SortableContainer(({ items2, selectedItems2 }) => {
    return (
        <ul className={"m-0 p-0"}>
            {items2.map((value2, index) => (
                <SortableItem2
                    key={`item-${value2.id}`}
                    index={index}
                    value2={value2}
                    sortIndex={index}
                    selectedItems2={selectedItems2}
                />
            ))}
        </ul>
    );
});

// Coluna 3
const SortableItem3 = SortableElement(({ value3, sortIndex, selectedItems3 }) => {
    return (
        <div className={getCardBackgroundClasses(value3.right3, selectedItems3, sortIndex)}>
            {value3.en.split("/")[2]}
        </div>
    )
});

const SortableList3 = SortableContainer(({ items3, selectedItems3 }) => {
    return (
        <ul className={"m-0 p-0"}>
            {items3.map((value3, index) => (
                <SortableItem3
                    key={`item-${value3.id}`}
                    index={index}
                    value3={value3}
                    sortIndex={index}
                    selectedItems3={selectedItems3}
                />
            ))}
        </ul>
    );
});

// Coluna 4
const SortableItem4 = SortableElement(({ value4, sortIndex, selectedItems4 }) => {
    return (
        <div className={getCardBackgroundClasses(value4.right4, selectedItems4, sortIndex)}>
            {value4.en.split("/")[3]}
        </div>
    )
});

const SortableList4 = SortableContainer(({ items4, selectedItems4 }) => {
    return (
        <ul className={"m-0 p-0"}>
            {items4.map((value4, index) => (
                <SortableItem4
                    key={`item-${value4.id}`}
                    index={index}
                    value4={value4}
                    sortIndex={index}
                    selectedItems4={selectedItems4}
                />
            ))}
        </ul>
    );
});

const Maze = ({
    blockData,
    step,
    contentIndex,
    lessonId,
    totalIndexes,
    setForceAudio,
    audioUrl
}) => {
    const [items1, setItems1] = useState([]);
    const [items2, setItems2] = useState([]);
    const [items3, setItems3] = useState([]);
    const [items4, setItems4] = useState([]);
    const [initialItems1, setInitialItems1] = useState([]);
    const [initialItems2, setInitialItems2] = useState([]);
    const [initialItems3, setInitialItems3] = useState([]);
    const [initialItems4, setInitialItems4] = useState([]);
    const [checked1, setChecked1] = useState(false);
    const [checked2, setChecked2] = useState(false);
    const [checked3, setChecked3] = useState(false);
    const [checked4, setChecked4] = useState(false);
    const [buttonMsg, setButtonMsg] = useState("Check");
    const [solved1, setSolved1] = useState(false);
    const [solved2, setSolved2] = useState(false);
    const [solved3, setSolved3] = useState(false);
    const [solved4, setSolved4] = useState(false);

    const selectedItemsInitialValue = {
        0: false,
        1: false,
        2: false,
        3: false,
    }

    const [selectedItems2, setSelectedItems2] = useState(selectedItemsInitialValue);
    const [selectedItems3, setSelectedItems3] = useState(selectedItemsInitialValue);
    const [selectedItems4, setSelectedItems4] = useState(selectedItemsInitialValue);

    // hook useRegisterProgress.
    const { registerProgress } = useRegisterProgress(
        blockData.block_id,
        lessonId,
        blockData.sectionId,
        blockData.section,
        blockData.num,
        totalIndexes,
        "maze"
    );

    const shuffle = (a) => {
        let j, x, i;
        for (i = a.length - 1; i > 0; i--) {
            j = Math.floor(Math.random() * (i + 1));
            x = a[i];
            a[i] = a[j];
            a[j] = x;
        }
        return a;
    }

    const getItems1 = () => {
        let items = [];

        items = blockData.content
            .filter(a => a.type !== "instruction")
            .map((item, index1) => {
                item.order1 = index1;
                item.right1 = undefined
                return item;
            });

        setItems1(items);
        setInitialItems1(items);
    }

    const getItems2 = () => {
        let items = [];

        items = blockData.content
            .filter(a => a.type !== "instruction")
            .map((item, index2) => {
                item.order2 = index2;
                item.right2 = undefined
                return item;
            });

        setItems2(items);
        shuffle(items)
        setInitialItems2(items);
    }

    const getItems3 = () => {
        let items = [];

        items = blockData.content
            .filter(a => a.type !== "instruction")
            .map((item, index3) => {
                item.order3 = index3;
                item.right3 = undefined
                return item;
            });

        setItems3(items);
        shuffle(items)
        setInitialItems3(items);
    }

    const getItems4 = () => {
        let items = [];

        items = blockData.content
            .filter(a => a.type !== "instruction")
            .map((item, index4) => {
                item.order4 = index4;
                item.right4 = undefined

                return item;
            });

        setItems4(items);
        shuffle(items)
        setInitialItems4(items);
    }

    const solve = () => {
        let localInitialItems1 = initialItems1;
        let localInitialItems2 = initialItems2;
        let localInitialItems3 = initialItems3;
        let localInitialItems4 = initialItems4;

        localInitialItems1 = localInitialItems1.map(item => {
            item.right1 = true;
            return item;
        });

        localInitialItems2 = localInitialItems2.map(item => {
            item.right2 = true;
            return item;
        });

        localInitialItems2.sort(function (a, b) {
            return a.order2 - b.order2;
        });

        localInitialItems3 = localInitialItems3.map(item => {
            item.right3 = true;
            return item;
        });

        localInitialItems3.sort(function (a, b) {
            return a.order3 - b.order3;
        });

        localInitialItems4 = localInitialItems4.map(item => {
            item.right4 = true;
            return item;
        });

        localInitialItems4.sort(function (a, b) {
            return a.order4 - b.order4;
        });

        setItems1(localInitialItems1);
        setItems2(localInitialItems2);
        setItems3(localInitialItems3);
        setItems4(localInitialItems4);
        setSolved1(true);
        setSolved2(true);
        setSolved3(true);
        setSolved4(true);
        setButtonMsg("Done");
    }

    const checkOrder = async () => {
        if (
            solved1 &&
            solved2 &&
            solved3 &&
            solved4
        ) {
            step(`forward`, true)
        }

        if (
            checked1 &&
            checked2 &&
            checked3 &&
            checked4
        ) {
            solve();
            return;
        }

        if (buttonMsg === "Check") {
            await registerProgress();
        }

        let localItems1 = items1;
        for (let i = 0; i < items1.length; i++) {
            localItems1[i].right1 = items1[i].order1 === i;
        }

        let localItems2 = items2;
        for (let i = 0; i < items2.length; i++) {
            localItems2[i].right2 = items2[i].order2 === i;
        }

        let localItems3 = items3;
        for (let i = 0; i < items3.length; i++) {
            localItems3[i].right3 = items3[i].order3 === i;
        }

        let localItems4 = items4;
        for (let i = 0; i < items4.length; i++) {
            localItems4[i].right4 = items4[i].order4 === i;
        }
        setForceAudio();

        setItems1(localItems1);
        setItems2(localItems2);
        setItems3(localItems3);
        setItems4(localItems4);
        setChecked1(true);
        setChecked2(true);
        setChecked3(true);
        setChecked4(true);
        setButtonMsg("Solve");
    }

    useEffect(() => {
        getItems1();
        getItems2();
        getItems3();
        getItems4();
    }, []);

    const updateBeforeSortStart2 = ({node, index, collection, isKeySorting}, event) => {
        setSelectedItems2({
            ...selectedItems2,
            [index]: true
        });
    };

    const updateBeforeSortStart3 = ({node, index, collection, isKeySorting}, event) => {
        setSelectedItems3({
            ...selectedItems3,
            [index]: true
        });
    };

    const updateBeforeSortStart4 = ({node, index, collection, isKeySorting}, event) => {
        setSelectedItems4({
            ...selectedItems4,
            [index]: true
        });
    };

    const onSortEnd2 = ({ oldIndex, newIndex }) => {
        setSelectedItems2({
            ...selectedItems2,
            [oldIndex]: false
        });
        setItems2(arrayMove(items2, oldIndex, newIndex))
    };

    const onSortEnd3 = ({ oldIndex, newIndex }) => {
        setSelectedItems3({
            ...selectedItems3,
            [oldIndex]: false
        });
        setItems3(arrayMove(items3, oldIndex, newIndex))
    };

    const onSortEnd4 = ({ oldIndex, newIndex }) => {
        setSelectedItems4({
            ...selectedItems4,
            [oldIndex]: false
        });
        setItems4(arrayMove(items4, oldIndex, newIndex))
    };

    return (
        <div className="default-card">
            {blockData.content[contentIndex].type === "instruction" ? (
                <LessonInstruction
                  instructionText={blockData.content[contentIndex].instruction}
                  audioUrl={audioUrl}
                />
            ) : (
                <div className="maze-illustrated-container">
                    <div className="title-container">
                        <div>
                            <h4>{t("Drag vertically to put in order.")}</h4>
                        </div>
                    </div>
                    <div className="illustrated-words-container drag-drop-list maze-illustrated-words-container">
                        <div className="maze-sortable-list-container">
                            <SortableList1
                                items1={items1}
                            />
                        </div>
                        <div className="maze-sortable-list-container">
                            <SortableList2
                                items2={items2}
                                updateBeforeSortStart={updateBeforeSortStart2}
                                onSortEnd={onSortEnd2}
                                selectedItems2={selectedItems2}
                            />
                        </div>
                        <div className="maze-sortable-list-container">
                            <SortableList3
                                items3={items3}
                                updateBeforeSortStart={updateBeforeSortStart3}
                                onSortEnd={onSortEnd3}
                                selectedItems3={selectedItems3}
                            />
                        </div>
                        <div className="maze-sortable-list-container">
                            <SortableList4
                                items4={items4}
                                updateBeforeSortStart={updateBeforeSortStart4}
                                onSortEnd={onSortEnd4}
                                selectedItems4={selectedItems4}
                            />
                        </div>
                    </div>
                    <div className="listen-drag-check-maze-container w-100 p-0">
                        <button
                            className={"maze-check-button m-0"}
                            onClick={() => {
                                checkOrder();
                            }}
                        >
                            {buttonMsg}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Maze;
